import React, { useState, useEffect } from "react"
import { RxCaretSort } from "react-icons/rx"
import { useSelector } from "react-redux"
import { convertMinutesTo12HHMM } from "../../../utils/generalUtils"
import { usePermissionsList } from "../../../hooks/queries/usePermissions"
import {
	getTimeForTable,
	currentlyRealTimeOngoing,
	currentlyRealTimeCompleted,
	currentlyRealTimeFuture,
	endTimeAllCases,
	allowedToEdit,
	hasPermission
} from "../../../utils/realTimeUtils"
import EditPatient from "../EditPatient"
import PatientInfoDialog from "../../FutureHuddles/PatientInfoDialog"
import {
	getScheduleSelection,
	getScheduleForSelection
} from "../../DailyCalendarFiles/CustomCalendar/scheduleSelection"
import useLogging from "../../../hooks/useLogging"

const PatientTableComponent = ({
	filteredData,
	nurseSchedule,
	chairSchedule,
	setNotification,
	query,
	sortConfig,
	setSortConfig,
	visibleColumns = null,
	onColumnsChange = null,
	resetPaginationRef = null,
	availableColumnsFromParent = null,
	mode = "current",
	isToday = true
}) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)
	const currentSchedule = getScheduleForSelection(scheduleSelection)
	const logging = useLogging()

	const [isEditOpen, setIsEditOpen] = useState(false)
	const [taskToEdit, setTaskToEdit] = useState(null)
	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage, setItemsPerPage] = useState(20)
	const [isPatientInfoOpen, setIsPatientInfoOpen] = useState(false)
	const [selectedPatient, setSelectedPatient] = useState(null)
	const [allAvailableColumns, setAllAvailableColumns] = useState([
		{ id: "nurse", name: "Nurse Name", field: "assignedNurse", sortable: true, default: true },
		{ id: "chair", name: "Chair", field: "assignedChair", sortable: true, default: true },
		{
			id: "patient",
			name: "Patient Name",
			field: "patientName",
			sortable: true,
			default: true
		},
		{
			id: "startTime",
			name: "Start Time",
			field: "actualStartTime",
			sortable: true,
			default: true
		},
		{ id: "endTime", name: "End Time", field: "actualEndTime", sortable: true, default: true },
		{ id: "length", name: "Infusion Length", field: "length", sortable: true, default: true },
		{
			id: "infusionType",
			name: "Infusion Type",
			field: "infusionType",
			sortable: true,
			default: true
		},
		{
			id: "reasonForVisit",
			name: "Reason for Visit",
			field: "reasonForVisit",
			sortable: true,
			default: true
		},
		{
			id: "referringDepartment",
			name: "Referring Department",
			field: "department",
			sortable: true,
			default: true
		},
		{ id: "acuity", name: "Acuity", field: "acuity", sortable: true, default: true },
		{
			id: "clinicAppointment",
			name: "Clinic Appointment",
			field: "clinicAppointmentTime",
			sortable: true,
			default: false
		},
		{
			id: "arrivalTime",
			name: "Arrival Time",
			field: "arrivalTime",
			sortable: true,
			default: false
		},
		{
			id: "bookedDate",
			name: "Booked Date",
			field: "bookedDate",
			sortable: true,
			default: false
		},
		{
			id: "visitStatus",
			name: "Visit Status",
			field: "internalStatus",
			sortable: true,
			default: false
		},
		{
			id: "clinicProvider",
			name: "Clinic Provider",
			field: "clinicProvider",
			sortable: true,
			default: false
		},
		{
			id: "linkedClinicAppointment",
			name: "Clinic Appointment Type",
			field: "linkedClinicAppointment",
			sortable: true,
			default: false
		},
		{
			id: "notes",
			name: "Notes",
			field: "notes",
			sortable: false,
			default: false
		}
	])

	const [activeColumns, setActiveColumns] = useState(
		visibleColumns || allAvailableColumns.filter(col => col.default).map(col => col.id)
	)

	const permissions = usePermissionsList()
	const isEditingPatient = useSelector(state => state.schedule.isEditing)

	// Update allAvailableColumns if parent provides them
	useEffect(() => {
		if (availableColumnsFromParent) {
			// Convert parent column format to match local format
			const convertedColumns = availableColumnsFromParent.map(col => ({
				id: col.id,
				name: col.name,
				field: col.field,
				sortable: col.dataType !== "calculation" && col.id !== "notes",
				default: col.default
			}))
			setAllAvailableColumns(convertedColumns)
		}
	}, [availableColumnsFromParent])

	useEffect(() => {
		if (visibleColumns) {
			setActiveColumns(visibleColumns)
		}
	}, [visibleColumns])

	useEffect(() => {
		if (resetPaginationRef) {
			resetPaginationRef.current = () => setCurrentPage(1)
		}
	}, [resetPaginationRef])

	useEffect(() => {
		setCurrentPage(1)
	}, [query])

	const handleSort = key => {
		let direction = "asc"
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc"
		}
		setSortConfig(key, direction)
	}

	const openEditPopup = task => {
		if (isEditingPatient) {
			return
		}

		// If in future mode, show patient info dialog instead of edit dialog
		if (mode === "future") {
			setSelectedPatient(task)
			setIsPatientInfoOpen(true)
			return
		}

		// Current mode behavior (edit dialog)
		if (
			!sandbox &&
			(hasPermission(permissions, "write:schedules") ||
				hasPermission(permissions, "write:chairs") ||
				hasPermission(permissions, "read:schedule-personal") ||
				sandbox) &&
			!allowedToEdit(task, sandbox, isTomorrowMode)
		) {
			setNotification({
				visible: true,
				message: "You cannot edit this visit because it has ended.",
				success: false
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} else {
			logging.info("Opened Edit Patient Popup", {
				patient: task,
				nurse: nurseSchedule.find(nurse => nurse.nurseId === task.assignedNurse)
			})
			setTaskToEdit(task)
			setIsEditOpen(true)
		}
	}

	const closeEditPopup = () => {
		setIsEditOpen(false)
		setTaskToEdit(null)
	}

	const closePatientInfoDialog = () => {
		setIsPatientInfoOpen(false)
		setSelectedPatient(null)
	}

	const handlePageChange = newPage => {
		setCurrentPage(newPage)
	}

	const handleItemsPerPageChange = e => {
		setItemsPerPage(Number(e.target.value))
		setCurrentPage(1)
	}

	// Calculate the number of items shown on the current page
	const startItemIndex = (currentPage - 1) * itemsPerPage
	const endItemIndex = Math.min(startItemIndex + itemsPerPage, filteredData.length)
	const itemsOnCurrentPage = endItemIndex - startItemIndex

	const highlightMatch = (text, query) => {
		if (!query) return text // Return original text if no query
		const parts = text.split(new RegExp(`(${query})`, "gi")) // Split text by query
		return parts.map(
			(part, index) =>
				part.toLowerCase() === query.toLowerCase() ? (
					<span key={index} className="sensitive bg-yellow-300">
						{part}
					</span>
				) : (
					part
				) // Highlight match
		)
	}

	const getAcuityColor = acuity => {
		switch (acuity) {
			case 0:
				return "bg-green-100 text-green-700 ring-green-300/20"
			case 1:
				return "bg-green-200 text-green-800 ring-green-400/20"
			case 2:
				return "bg-green-300 text-green-900 ring-green-500/20"
			case 3:
				return "bg-yellow-200 text-yellow-800 ring-yellow-400/20"
			case 4:
				return "bg-orange-200 text-orange-800 ring-orange-400/20"
			case 5:
				return "bg-red-200 text-red-800 ring-red-500/20"
			default:
				return "bg-gray-100 text-gray-700 ring-gray-400/20"
		}
	}

	const getCostInformationColor = costInfo => {
		switch (costInfo) {
			case "Low":
				return "bg-green-100 text-green-700 ring-green-300/20"
			case "Medium":
				return "bg-yellow-100 text-yellow-700 ring-yellow-300/20"
			case "High":
				return "bg-orange-100 text-orange-700 ring-orange-300/20"
			case "Ultra-High":
				return "bg-red-100 text-red-700 ring-red-300/20"
			default:
				return "bg-gray-100 text-gray-700 ring-gray-400/20"
		}
	}

	const getIssueSeverityColor = severity => {
		switch (severity) {
			case "error":
				return "bg-red-100 text-red-800"
			case "warning":
				return "bg-yellow-100 text-yellow-800"
			case "info":
				return "bg-blue-100 text-blue-800"
			default:
				return "bg-gray-100 text-gray-800"
		}
	}

	// Helper to render cell content based on column type
	const renderCellContent = (columnId, data) => {
		switch (columnId) {
			case "nurse":
				return (
					nurseSchedule.find(n => n.nurseId === data.assignedNurse)?.nurseName ||
					"Unknown Nurse"
				)
			case "chair":
				return (
					chairSchedule.find(chair => chair.chairId === data.assignedChair)?.chairName ||
					"Unknown Chair"
				)
			case "patient":
				return (
					<div className="sensitive ph-no-capture text-lg font-semibold text-gray-800">
						{highlightMatch(data.patientName, query)}
					</div>
				)
			case "startTime":
				return (
					<div className="flex items-center">
						<span>{convertMinutesTo12HHMM(getTimeForTable(data))}</span>
						{currentlyRealTimeOngoing(data) ? (
							<span className="animate-pulse ml-2 h-3 w-3 rounded-full bg-green-500"></span>
						) : null}
					</div>
				)
			case "endTime":
				return convertMinutesTo12HHMM(endTimeAllCases(data, sandbox, isTomorrowMode))
			case "length":
				return `${Math.floor(data.length / 60)} hrs`
			case "acuity":
				return (
					<span
						className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset ${getAcuityColor(
							data.acuity
						)}`}
					>
						{data.acuity}
					</span>
				)
			case "costInformation":
				return (
					<span
						className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset ${getCostInformationColor(
							data.costInformation
						)}`}
					>
						{data.costInformation || "N/A"}
					</span>
				)
			case "clinicAppointment":
				return data.clinicAppointmentTime
					? convertMinutesTo12HHMM(data.clinicAppointmentTime)
					: "N/A"
			case "arrivalTime":
				return data.arrivalTime ? convertMinutesTo12HHMM(data.arrivalTime) : "N/A"
			case "bookedDate":
				return data.bookedDate || "N/A"
			case "visitStatus":
				return data.internalStatus || "N/A"
			case "clinicProvider":
				return data.clinicProvider || "N/A"
			case "linkedClinicAppointment":
				return data.linkedClinicAppointment || "N/A"
			case "notes":
				return data.notes ? <div className="max-w-xs truncate">{data.notes}</div> : "N/A"
			case "infusionType":
				return data.infusionType || "N/A"
			case "reasonForVisit":
				return data.reasonForVisit || "N/A"
			case "referringDepartment":
				return data.department || "N/A"
			case "scheduleIssues":
				if (
					mode === "future" &&
					data.scheduleIssuesArray &&
					data.scheduleIssuesArray.length > 0
				) {
					// Future mode - render clickable badges
					return (
						<div className="flex flex-wrap gap-1">
							{data.scheduleIssuesArray.map((issue, index) => (
								<span
									key={index}
									className={`inline-flex items-center cursor-pointer rounded-md px-2 py-1 text-sm font-medium ${getIssueSeverityColor(issue.severity || "info")}`}
									onClick={e => {
										e.stopPropagation()
										setSelectedPatient({
											...data,
											selectedIssues: data.scheduleIssuesArray
										})
										setIsPatientInfoOpen(true)
									}}
								>
									{issue.type || "Issue"}
								</span>
							))}
						</div>
					)
				}
				return data.scheduleIssuesText || "None"
			default:
				const column = allAvailableColumns.find(col => col.id === columnId)
				return column && column.field && data[column.field] ? data[column.field] : "N/A"
		}
	}

	// Helper function to determine cell styling for time-related columns
	const getTimeCellStyle = (columnId, data) => {
		if (columnId === "startTime" || columnId === "endTime") {
			// If not today's data, default to black for all times
			if (!isToday) {
				return "text-gray-800"
			}

			return `${
				currentlyRealTimeCompleted(data)
					? "text-blue-500"
					: currentlyRealTimeOngoing(data)
						? "text-green-500"
						: currentlyRealTimeFuture(data, sandbox, isTomorrowMode)
							? "text-gray-800"
							: "text-gray-400"
			}`
		}
		return "text-gray-800"
	}

	if (!permissions) {
		return null
	}

	// For each column in activeColumns, find the corresponding column definition
	const getColumnName = columnId => {
		const column = allAvailableColumns.find(col => col.id === columnId)
		return column ? column.name : columnId
	}

	const getColumnSortKey = columnId => {
		const column = allAvailableColumns.find(col => col.id === columnId)
		return column ? column.field : null
	}

	const isColumnSortable = columnId => {
		const column = allAvailableColumns.find(col => col.id === columnId)
		return column ? column.sortable : false
	}

	return (
		<div className="flex flex-col pb-16 relative">
			{/* Table */}
			<div className="overflow-x-auto flex-grow">
				<div className="table-auto w-full text-sm">
					<div className="mx-auto bg-white">
						<div className="rounded-xl">
							<table className="bg-white w-full">
								<thead className="bg-gray-100">
									<tr>
										{activeColumns.map(columnId => (
											<th
												key={columnId}
												scope="col"
												className="px-6 py-6 text-left text-sm font-semibold text-gray-600 uppercase cursor-pointer"
												onClick={() => {
													const sortKey = getColumnSortKey(columnId)
													if (sortKey && isColumnSortable(columnId)) {
														handleSort(sortKey)
													}
												}}
											>
												<div className="flex items-center">
													{getColumnName(columnId)}
													{isColumnSortable(columnId) && (
														<RxCaretSort
															className={`ml-1 text-lg ${sortConfig.key === getColumnSortKey(columnId) ? "text-customBlue" : "text-gray-400"}`}
														/>
													)}
													{columnId === "startTime" && (
														<div className="flex items-center relative group">
															<span className="inline-flex items-center justify-center w-6 h-6 text-white bg-customBlue rounded-full cursor-pointer ml-1">
																?
															</span>
															<div className="absolute top-[-2rem] left-1/2 transform -translate-x-1/2 w-48 bg-gray-700 text-white text-sm rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
																<ul className="list-disc pl-5">
																	<li>Blue is completed</li>
																	<li>Green is ongoing</li>
																	<li>Black is not started</li>
																	<li>
																		Gray is no show or cancelled
																	</li>
																</ul>
															</div>
														</div>
													)}
												</div>
											</th>
										))}
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{filteredData.slice(startItemIndex, endItemIndex).map(data => (
										<tr
											key={data.patientId}
											className="hover:bg-gray-50 cursor-pointer"
											onClick={() => openEditPopup(data)}
										>
											{activeColumns.map(columnId => (
												<td
													key={columnId}
													className={`px-6 py-4 whitespace-nowrap text-left text-lg ${getTimeCellStyle(
														columnId,
														data
													)}`}
												>
													{renderCellContent(columnId, data)}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{isEditOpen && mode === "current" && taskToEdit && (
							<EditPatient
								patient={taskToEdit}
								isOpen={
									(hasPermission(permissions, "write:schedules") ||
										hasPermission(permissions, "write:chairs") ||
										hasPermission(permissions, "read:schedule-personal") ||
										sandbox) &&
									taskToEdit !== null &&
									allowedToEdit(taskToEdit, sandbox, isTomorrowMode)
								}
								onClose={closeEditPopup}
								schedule={currentSchedule}
								scheduleSelection={scheduleSelection}
								permissions={permissions}
								setNotification={setNotification}
							/>
						)}

						{mode === "future" && (
							<PatientInfoDialog
								isOpen={isPatientInfoOpen}
								onClose={closePatientInfoDialog}
								patient={selectedPatient || {}}
								issues={selectedPatient?.scheduleIssuesArray || []}
							/>
						)}
					</div>
				</div>
			</div>

			{/* Pagination */}
			<div className="px-6 py-10 flex justify-between items-center bg-white sticky bottom-0 z-0">
				<div className="flex items-center">
					<span className="text-sm text-gray-600 mr-2">View</span>
					<select
						value={itemsPerPage}
						onChange={handleItemsPerPageChange}
						className="h-9 bg-gray-100 text-sm text-black rounded-md py-2 border border-transparent focus:outline-none focus:ring-0 focus:border-transparent shadow-sm transition ease-in-out duration-150 mr-2"
					>
						{filteredData.length >= 9 && <option value={9}>9</option>}
						{filteredData.length >= 20 && <option value={20}>20</option>}
						{filteredData.length >= 50 && <option value={50}>50</option>}
						{filteredData.length < 9 && (
							<option value={filteredData.length}>{filteredData.length}</option>
						)}
					</select>
					<span className="text-sm text-gray-600">Patients</span>
				</div>
				<div className="inline-flex items-center">
					<span className="text-sm text-gray-600 mr-1">Showing</span>
					<span className="text-sm text-black mr-1">{startItemIndex + 1}</span>
					<span className="text-sm text-gray-600 mr-1">to</span>
					<span className="text-sm text-black mr-1">
						{startItemIndex + itemsOnCurrentPage}
					</span>
					<span className="text-sm text-gray-600 mr-2">
						of {filteredData.length} Patients
					</span>
					<div className="inline-flex items-center space-x-4">
						<button
							type="button"
							className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none"
							onClick={() => handlePageChange(currentPage - 1)}
							disabled={currentPage === 1}
						>
							&lt;
						</button>
						<span className="px-3 py-1.5 text-gray-700 bg-gray-100 rounded-lg">
							{currentPage}
						</span>
						<span className="text-gray-500">of</span>
						<span className="text-gray-500">
							{Math.ceil(filteredData.length / itemsPerPage)}
						</span>
						<button
							type="button"
							className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none"
							onClick={() => handlePageChange(currentPage + 1)}
							disabled={currentPage * itemsPerPage >= filteredData.length}
						>
							&gt;
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default PatientTableComponent
